import { useCallback, useEffect, useState } from 'react'
import { Delete, Lock } from 'lucide-react'

import { ApiRequestError } from '../lib/apiClient'
import { cn } from '../lib/cn'
import { formatDuration } from '../lib/format'
import { t } from '../lib/i18n'

import { Modal } from './overlays'
import { usePriceLock } from './PriceLock'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'] as const

/**
 * The PIN pad behind Price Lock.
 *
 * It unlocks the SESSION, not a figure. Nothing on the page is uncovered —
 * `POST /price-lock/unlock` sets `money_unlocked_until` on the session, the
 * caches are dropped, and the next render simply asks for money the payload now
 * contains. The PIN is exactly four digits and is checked server-side; a wrong
 * one comes back as the server's own refusal, which is shown as it comes.
 */
export function PinSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { unlock, autoLockSeconds } = usePriceLock()
  const [digits, setDigits] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setDigits('')
      setError(null)
    }
  }, [open])

  const submit = useCallback(
    async (pin: string) => {
      setBusy(true)
      setError(null)
      try {
        await unlock(pin)
        onClose()
      } catch (cause) {
        setError(cause instanceof ApiRequestError ? cause.message : t('state.errorTitle'))
        setDigits('')
      } finally {
        setBusy(false)
      }
    },
    [unlock, onClose],
  )

  useEffect(() => {
    if (digits.length < 4 || busy) return
    const id = window.setTimeout(() => void submit(digits), 120)
    return () => window.clearTimeout(id)
  }, [digits, busy, submit])

  return (
    // How long the unlock lasts is the tenant's own `autoLockSeconds`, out of
    // `/me` — it was five minutes here because the design deck said five.
    <Modal
      open={open}
      onClose={onClose}
      title={t('pin.title')}
      subtitle={t('pin.subtitle', { duration: formatDuration(autoLockSeconds) })}
    >
      <div className="mx-auto max-w-[300px]">
        <div className="flex items-center justify-center gap-3.5 py-5" aria-hidden="true">
          {[0, 1, 2, 3].map((index) => (
            <span
              key={index}
              className={cn(
                'size-3.5 rounded-full',
                index < digits.length ? 'bg-accent' : 'bg-muted/25',
              )}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {KEYS.map((key, index) =>
            key === '' ? (
              <span key={`gap-${index}`} />
            ) : (
              <button
                key={key}
                type="button"
                disabled={busy}
                aria-label={key === 'back' ? t('common.back') : key}
                onClick={() =>
                  setDigits((current) =>
                    key === 'back' ? current.slice(0, -1) : (current + key).slice(0, 4),
                  )
                }
                className={cn(
                  'flex h-14 items-center justify-center rounded-[14px] disabled:opacity-45',
                  'gl font-display text-xl font-bold text-dark',
                )}
              >
                <span className="z flex items-center justify-center">
                  {key === 'back' ? <Delete className="size-5 text-muted" /> : key}
                </span>
              </button>
            ),
          )}
        </div>
        {error ? (
          <p
            role="alert"
            className="mt-3 rounded-r border border-danger/30 bg-danger/10 p-2.5 text-center text-[12.5px] leading-relaxed text-danger"
          >
            {error}
          </p>
        ) : null}
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted">
          <Lock className="size-3.5" />
          {t('money.locked')}
        </p>
      </div>
    </Modal>
  )
}
